/**
 * ModelCard — owner-rail card shown in place of the 5-slot session face grid once a
 * subject has a trained, ready model.
 *
 * Mounted by both StudioRail (desktop aside) and StudioSheet (mobile bottom sheet) when
 * `userModel?.status === 'ready'` — the caller is responsible for that gate; ModelCard
 * itself always renders the card. "use other photos instead" hands control back to the
 * caller, which reveals the classic session-slot grid (StudioFaces) in its place.
 *
 * Design canvas: thinking/design/handoffs/training_shift/Portrait Deltas and Errors.html
 * (owner rail section). Backend is `getUserModel` — see
 * webapp/app/core/products/trainingShift.server.ts — which reads the user's `user_models`
 * row; this component lights up once a user has a training or ready model.
 */

import type {UserModel} from '@core/products/trainingShift.server'
import styles from './ModelCard.module.scss'

/** As many thumbs as the rail card holds on one line. The set is 12-30 photos; the strip is a
 *  glance at it, and the exact count is already stated in the meta line. */
const MAX_STRIP_THUMBS = 5

export interface ModelCardProps {
    /** Only what the card actually draws — so both callers fit without conversion: the portrait
     *  rail passes the loader's `UserModel` (Date), the pack workbench its serialized twin (ISO
     *  string). Borrowing either full shape would drag in fields the card never reads (percent,
     *  startedAt) whose types differ across the two. */
    model: {
        trainedAt: Date | string
        photoCount: number
        status: UserModel['status']
        thumbUrls: string[]
    }
    /** Omitted where there is nothing to fall back to — the pack owner workbench has no face slots,
     *  so it renders the card without the alternative-photos link. */
    onUseOtherPhotos?: () => void
    /** `rail` (default): the narrow studio rail — everything stacks. `wide`: the pack's left
     *  column, where the card spans the CTA's width — the identity line and the ready chip share
     *  the top row rather than leaving the right half empty. */
    layout?: 'rail' | 'wide'
}

/** "trained 12 July · 14 photos" — day + month, no ordinal (canvas copy is literal).
 *  Exported so other model-possession surfaces (YourModelBlock, canvas 03) share the same format. */
export function formatTrainedAt(date: Date): string {
    const months = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December',
    ]
    return `${date.getDate()} ${months[date.getMonth()]}`
}

export function ModelCard({model, onUseOtherPhotos, layout = 'rail'}: ModelCardProps) {
    const isWide = layout === 'wide'
    return (
        <div className={`${styles.card} ${isWide ? styles.cardWide : ''}`}>
            <div className={styles.top}>
                <svg
                    width="26"
                    height="26"
                    viewBox="0 0 64 64"
                    fill="none"
                    aria-hidden="true"
                    className={styles.mark}
                >
                    <ellipse cx="32" cy="14" rx="6.5" ry="14" fill="#FAFAF9"/>
                    <ellipse cx="32" cy="14" rx="6.5" ry="14" fill="#FAFAF9" transform="rotate(60 32 32)"/>
                    <ellipse cx="32" cy="14" rx="6.5" ry="14" fill="#FAFAF9" transform="rotate(120 32 32)"/>
                    <ellipse cx="32" cy="14" rx="6.5" ry="14" fill="#FAFAF9" transform="rotate(180 32 32)"/>
                    <ellipse cx="32" cy="14" rx="6.5" ry="14" fill="#FAFAF9" transform="rotate(240 32 32)"/>
                    <ellipse cx="32" cy="14" rx="6.5" ry="14" fill="#FAFAF9" transform="rotate(300 32 32)"/>
                    <circle cx="32" cy="32" r="9" fill="#E8C24A"/>
                    <circle cx="32" cy="32" r="9" fill="none" stroke="#FAFAF9" strokeWidth="0.8"/>
                </svg>
                <div>
                    <h5 className={styles.title}>Your model</h5>
                    <p className={styles.meta}>
                        trained {formatTrainedAt(new Date(model.trainedAt))} · {model.photoCount} photos
                    </p>
                </div>
                {/* Wide: the chip rides the top row's far edge instead of starting a line of its
                    own, so a full-width card doesn't read as half-empty. */}
                {isWide && model.status === 'ready' && (
                    <span className={`${styles.chip} ${styles.topChip}`}>● ready</span>
                )}
            </div>

            {!isWide && model.status === 'ready' && (
                <p className={styles.chipLine}>
                    <span className={styles.chip}>● ready</span>
                </p>
            )}

            {/* Contact strip: a glance at the set, not the set. A training set runs 12-30 photos;
                rendering all of them burst the rail card (observed on a 30-photo model). The count
                already lives in the meta line above. */}
            {model.thumbUrls.length > 0 && (
                <div className={styles.miniStrip}>
                    {model.thumbUrls.slice(0, MAX_STRIP_THUMBS).map((url) => (
                        <img key={url} src={url} alt="" className={styles.thumb} />
                    ))}
                </div>
            )}

            {onUseOtherPhotos && (
                <a
                    href="#"
                    className={styles.altLink}
                    onClick={(e) => {
                        e.preventDefault()
                        onUseOtherPhotos()
                    }}
                >
                    use other photos instead →
                </a>
            )}
        </div>
    )
}
